/**
 * Knopf „Restzahlung anlegen" in der Veranstaltung (Antoine 14.10.2026): erzeugt die
 * Restzahlungsrechnung sofort und schickt sie dem Gast — OHNE Bestätigungsdialog. Derselbe Weg wie
 * die Automatik 14 Tage vor dem Termin (POST /api/events/:id/restzahlungsrechnung). Der Betrag
 * kommt vom Server aus dem Zahlungsplan, nie von hier.
 *
 * Sichtbar nur für die Leitung, bei einer offenen Restzahlung und nicht bei abgesagten Vorgängen.
 */
import { useState } from "react";
import { apiMutate } from "../../hooks/useApi";
import type { ApiError } from "../../lib/api";
import type { EventStatus, Payment } from "../types";
import { Button, ErrorNote, Icon } from "../components";
import { istRestzahlungHinweisCode, type RestzahlungHinweisDaten } from "./ZahlungslinkVersand";

type RestzahlungAntwort = { invoiceNumber: string | null; gesendet: boolean };

export function zeigeRestzahlungKnopf(payments: Payment[], status: EventStatus, istLeitung: boolean): boolean {
  if (!istLeitung || status === "cancelled" || status === "refunded") return false;
  return payments.some((p) => p.paymentType === "final" && (p.status === "pending" || p.status === "processing"));
}

export function RestzahlungAnlegenKnopf({ eventId, onErstellt, onHinweis }: { eventId: string; onErstellt?: () => void; onHinweis?: (h: RestzahlungHinweisDaten | null) => void }) {
  const [busy, setBusy] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const [ergebnis, setErgebnis] = useState<RestzahlungAntwort | null>(null);

  if (ergebnis) {
    return (
      <p role="status" className="flex items-start gap-1.5 rounded-lg bg-[#F0EAE0] px-2.5 py-1.5 font-body-sm text-[11.5px] text-[#6F675A] dark:bg-white/[0.08]">
        <Icon name="check" className="mt-px shrink-0 text-[14px]" />
        <span className="min-w-0 break-words">
          Restzahlungsrechnung{ergebnis.invoiceNumber ? ` ${ergebnis.invoiceNumber}` : ""} erstellt
          {ergebnis.gesendet ? " und an den Gast geschickt." : " – der Versand an den Gast ist fehlgeschlagen, bitte in der Abrechnung erneut senden."}
        </span>
      </p>
    );
  }

  const anlegen = async () => {
    setBusy(true);
    setFehler(null);
    onHinweis?.(null);
    try {
      const r = await apiMutate<RestzahlungAntwort>(`/api/events/${eventId}/restzahlungsrechnung`, "POST");
      setErgebnis(r);
      onHinweis?.(null);
      onErstellt?.();
    } catch (e) {
      const code = (e as ApiError).code;
      const message = (e as ApiError).message || "Die Restzahlungsrechnung konnte nicht erstellt werden.";
      // P4: bekannte Gründe erscheinen als ruhiger Hinweis oben in der Zahlungen-Karte, mit Weg.
      if (onHinweis && istRestzahlungHinweisCode(code)) onHinweis({ code, message });
      else setFehler(message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-2">
      <Button type="button" variant="secondary" className="!py-1.5 !text-[11.5px]" disabled={busy} onClick={() => void anlegen()}>
        <Icon name="receipt_long" className="text-[15px]" /> {busy ? "Wird angelegt …" : "Restzahlung anlegen"}
      </Button>
      {fehler && <ErrorNote message={fehler} />}
    </div>
  );
}
